// Outside `/compliance` and the sidebar layout on purpose: its readers are any org
// user, including employees with no compliance permission or capability.
export const TEXT_DOCUMENT_READER_ROUTE = '/text-documents/view'

// Authoring routes. Under `/compliance` rather than the Standard Editor because
// everyone with MANAGE_COMPLIANCE authors here, not only backoffice admins.
// `EDIT` is the open draft, `VIEW` any immutable version (`?version=` defaults to latest).
export const TEXT_DOCUMENT_ROUTES = {
  EDIT: '/compliance/text-documents/edit',
  VIEW: '/compliance/text-documents/view',
} as const

// The page the editor returns to (path plus query), set by whoever opened it. The
// document drawer opens over documents, controls, tasks and more, so neither the
// route nor the user's permissions can tell where the visit came from.
export const TEXT_DOCUMENT_ORIGIN_PARAM = 'from'

export type ITextDocumentOrigin = string

// Set by the mention email: the editor opens on this comment thread.
export const TEXT_DOCUMENT_THREAD_PARAM = 'thread'

// The editor routes' search params, so neither page has to spell the key again.
export interface ITextDocumentOriginParams {
  [TEXT_DOCUMENT_ORIGIN_PARAM]?: string
}

// In-app paths only, or `?from=` is an open redirect (`//host` and `/\host` leave the site).
export function isTextDocumentOrigin(value: string | null | undefined): value is ITextDocumentOrigin {
  return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/\\')
}

// Keeps any params already on the URL, so `?version=` survives.
export function withTextDocumentOrigin(url: string, origin: ITextDocumentOrigin | null): string {
  if (!origin) return url
  return `${url}${url.includes('?') ? '&' : '?'}${TEXT_DOCUMENT_ORIGIN_PARAM}=${encodeURIComponent(origin)}`
}

export const DOCUMENT_TYPES = {
  POLICY: 'policy',
  DOCUMENT: 'document',
  REGISTER: 'register',
  PROCESS: 'process',
  PROCEDURE: 'procedure',
} as const

// GDOCS-SUNSET(orgs-migrated): both kinds describe importing a Google Doc, and neither
// has a meaning once nothing is imported. Goes with `providers/blocknote/fidelity.ts`,
// after `IConversionIssue` in `types/textDocumentConversion.ts`.
export const CONVERSION_ISSUE_KINDS = {
  // Retired in favour of TEXT_LOSS, but kept: `text_document_provisioning_log` rows
  // written before the readers moved to the HTML export still carry it.
  ROUND_TRIP_DIFF: 'roundTripDiff' as const,
  TEXT_LOSS: 'textLoss' as const,
  TITLE_MISMATCH: 'titleMismatch' as const,
  // The conversion produced a document but the fidelity check could not run on it, so
  // nobody has compared it against the source. Needs a human, same as a real loss.
  FIDELITY_UNAVAILABLE: 'fidelityUnavailable' as const,
} as const

export type IConversionIssueKind = (typeof CONVERSION_ISSUE_KINDS)[keyof typeof CONVERSION_ISSUE_KINDS]

export const DOCUMENT_CLASSIFICATIONS = ['Public', 'Internal', 'Confidential', 'Commercial in Confidence'] as const

export type IDocumentClassification = (typeof DOCUMENT_CLASSIFICATIONS)[number]

export const DEFAULT_DOCUMENT_CLASSIFICATION: IDocumentClassification = 'Internal'

// Presentation-only value for a document whose classification was never determined.
// Kept out of `DOCUMENT_CLASSIFICATIONS` so it never reaches the database enum.
export const UNCLASSIFIED = 'unclassified' as const

export type IDisplayClassification = IDocumentClassification | typeof UNCLASSIFIED

// Every outcome the Drive seeding script can report for one file, including the
// ones where nothing was written. `dryRun` and the write outcomes are mutually
// exclusive: a dry run never reaches `created` or `versionAdded`.
export const TEMPLATE_SEED_STATUS = {
  CREATED: 'created' as const,
  VERSION_ADDED: 'versionAdded' as const,
  SKIPPED_OPEN_DRAFT: 'skippedOpenDraft' as const,
  SKIPPED_UNSUPPORTED_FILE: 'skippedUnsupportedFile' as const,
  NO_CODE_IN_FILENAME: 'noCodeInFilename' as const,
  NO_CATALOG_DOCUMENT: 'noCatalogDocument' as const,
  DRY_RUN: 'dryRun' as const,
} as const

export type ITemplateSeedStatus = (typeof TEMPLATE_SEED_STATUS)[keyof typeof TEMPLATE_SEED_STATUS]

export const TEXT_DOCUMENT_PROVISIONING_SOURCE = {
  GOOGLE_DOC: 'googleDoc' as const,
  TEMPLATE: 'template' as const,
} as const

export type ITextDocumentProvisioningSource =
  (typeof TEXT_DOCUMENT_PROVISIONING_SOURCE)[keyof typeof TEXT_DOCUMENT_PROVISIONING_SOURCE]

// `skippedUnsupportedFormat` is a success, not a failure: the document is not a Google
// Doc and stays on Drive by design. `failed` is only for documents that should have
// converted and did not.
export const TEXT_DOCUMENT_PROVISIONING_STATUS = {
  CREATED: 'created' as const,
  CREATED_WITH_ISSUES: 'createdWithIssues' as const,
  FAILED: 'failed' as const,
  SKIPPED_EXISTING: 'skippedExisting' as const,
  SKIPPED_NO_TEMPLATE: 'skippedNoTemplate' as const,
  SKIPPED_HAS_GOOGLE_LINK: 'skippedHasGoogleLink' as const,
  SKIPPED_UNSUPPORTED_FORMAT: 'skippedUnsupportedFormat' as const,
} as const

export type ITextDocumentProvisioningStatus =
  (typeof TEXT_DOCUMENT_PROVISIONING_STATUS)[keyof typeof TEXT_DOCUMENT_PROVISIONING_STATUS]

// A draft cannot point at the reader route, which serves approved versions only, so
// it points at the authoring route instead. Approval promotes the link.
export const TEXT_DOCUMENT_LINK_TARGET = {
  READER: 'reader' as const,
  AUTHORING: 'authoring' as const,
  NONE: 'none' as const,
} as const

export type ITextDocumentLinkTarget = (typeof TEXT_DOCUMENT_LINK_TARGET)[keyof typeof TEXT_DOCUMENT_LINK_TARGET]

// Written to `text_document_versions.change_summary` for every version the
// provisioning loop creates, so anyone opening the version history sees immediately
// that the draft or approved copy came from a migration rather than being authored
// in the editor.
export const TEXT_DOCUMENT_MIGRATION_CHANGE_SUMMARY = 'Migrated from Google Docs'

// The template-sourced counterpart of the constant above, written for every version
// `provisionOrganizationTextDocuments` creates from the `template` source (Unit 4),
// so the version history never claims a brand-new organization's document was
// "migrated" from a Google Doc it never had.
export const TEXT_DOCUMENT_TEMPLATE_CHANGE_SUMMARY = 'Created from document template'
